import React, { useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import BaseModal from '../utils/BaseModal/BaseModal';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  AddMediaButton,
  AddMediaText,
  Container,
  Form,
  Label,
  SelectedMediaContainer,
  StyledButton,
  Text,
} from './UploadBundleModal.styles';
import { useDispatch } from 'react-redux';
import { uploadAssistedPhoto, uploadAssistedVideo, uploadBundle } from '../../../store/slices/user/asyncThunks';
import { notifyError, notifyInfo } from '../../../utils/notify';
import { ALBUM_MEDIA_TYPE, MEDIA_ASSET_SALE_TAG, UPLOAD_FILE_CONSTRAINTS } from '../../../utils/constants';
import UploadMediaButton from '../../UploadMediaButton/UploadMediaButton';
import Input from '../../utils/inputs/Input/Input';
import MediaItem from '../../MediaItem/MediaItem';
import { ReactComponent as ImagePlusIcon } from '../../../assets/icons/image-plus.svg';
import ErrorContainer from '../../utils/ErrorContainer/ErrorContainer';
import SelectMediaModal from '../SelectMediaModal/SelectMediaModal';

const uploadBundleSchema = yup
  .object()
  .shape({
    name: yup.string().trim().required('Name is required'),
    media: yup.array().min(5, 'You need to upload at least 5 items').max(10, 'You can upload up to 10 items only'),
  })
  .required();

const tags = [MEDIA_ASSET_SALE_TAG.SINGLE_PAID];

const UploadBundleModal = ({ modalRef, onSuccess, influencerId }) => {
  const dispatch = useDispatch();
  const [uploadErrors, setUploadErrors] = useState([]);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const selectMediaModalRef = useRef();
  const [reload, setReload] = useState(true);

  const {
    handleSubmit,
    control,
    formState: { errors },
    register,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      name: '',
      freePreviewMedia: null,
      media: [],
    },
    delayError: 300,
    resolver: yupResolver(uploadBundleSchema),
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'media' });

  const freePreviewMediaWatch = watch('freePreviewMedia');

  const onProgress = progress => {
    setProgress(progress > 99 ? 99 : progress.toFixed(1));
  };

  const onSubmit = async data => {
    setLoading(true);
    setReload(false);
    const name = data.name;
    const freePreviewMedia = data.freePreviewMedia;

    let representativeFilename = null;
    let representativeType = null;
    if (freePreviewMedia) {
      const freePreviewType = freePreviewMedia.file.type.includes('video')
        ? ALBUM_MEDIA_TYPE.VIDEO
        : ALBUM_MEDIA_TYPE.PHOTO;
      representativeType = freePreviewType;
      if (freePreviewType === ALBUM_MEDIA_TYPE.PHOTO) {
        const response = await dispatch(
          uploadAssistedPhoto({
            photo: freePreviewMedia,
            type: MEDIA_ASSET_SALE_TAG.BUNDLE,
            representativeMedia: true,
            onProgress,
            influencerId,
          }),
        ).unwrap();

        representativeFilename = response.fileName;
      } else {
        const response = await dispatch(
          uploadAssistedVideo({
            video: freePreviewMedia,
            type: MEDIA_ASSET_SALE_TAG.BUNDLE,
            representativeMedia: true,
            onProgress,
            influencerId,
          }),
        ).unwrap();
        representativeFilename = response.fileName;
      }
    }

    const bundleFiles = data.media.map(m => m.path);

    dispatch(
      uploadBundle({
        body: {
          name,
          ...(representativeFilename && {
            representativeMediaFilename: representativeFilename,
          }),
          ...(representativeType && {
            representativeMediaType: representativeType,
          }),
          media: bundleFiles,
        },
        influencerId,
      }),
    )
      .unwrap()
      .then(() => {
        modalRef.current.hide();
        onSuccess();
        setReload(true);
        notifyInfo('Bundle uploaded successfully');
      })
      .catch(error => {
        setLoading(false);
        setProgress(0);
        notifyError(error.message);
      });
  };

  const onSelectMedia = media => {
    remove();
    for (const m of media) {
      append(m);
    }
  };

  return (
    <>
      <BaseModal
        title="Upload Bundle"
        ref={modalRef}
        onClose={() => {
          modalRef.current.hide();
        }}
        onAfterClose={() => {
          reset();
          setProgress(0);
          setLoading(false);
        }}>
        <Container>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Input label="name" placeholder="Enter bundle name" {...register('name')} error={errors.name?.message} />
            <Label>Free Preview</Label>
            <Text>Optionally add free preview media</Text>
            {freePreviewMediaWatch ? (
              <MediaItem
                type={
                  freePreviewMediaWatch.file.type.includes('video') ? ALBUM_MEDIA_TYPE.VIDEO : ALBUM_MEDIA_TYPE.PHOTO
                }
                file={freePreviewMediaWatch}
                progress={progress}
                onDelete={() => {
                  setValue('freePreviewMedia', null);
                  setUploadErrors(prev => prev.filter(error => error.fileName !== freePreviewMediaWatch.file.name));
                }}
                uploadError={uploadErrors.find(error => error.fileName === freePreviewMediaWatch.file.name)}
              />
            ) : (
              <Controller
                name="freePreviewMedia"
                control={control}
                render={({ field }) => {
                  return (
                    <UploadMediaButton
                      disabled={loading}
                      type={UPLOAD_FILE_CONSTRAINTS.TYPE.MIXED}
                      onChange={files => {
                        field.onChange(files[0]);
                      }}
                      setError={setUploadErrors}
                      multiple={false}
                    />
                  );
                }}
              />
            )}
            <Label $marginTop={24}>Select Paid Media</Label>
            <Text>Select 5-10 pictures or videos that you want to sell in this bundle</Text>
            <SelectedMediaContainer>
              <AddMediaButton type="button" onClick={() => selectMediaModalRef.current.show()}>
                <ImagePlusIcon />
                <AddMediaText>Add Media</AddMediaText>
              </AddMediaButton>
              {fields.map((field, index) => {
                return (
                  <MediaItem
                    key={field.id}
                    kind={field.kind}
                    type={field.kind}
                    onClick={() => {}}
                    file={field}
                    onDelete={() => {
                      remove(index);
                    }}
                  />
                );
              })}
            </SelectedMediaContainer>
            <ErrorContainer errorText={errors.media?.message} />
            <StyledButton title="Save" isLoading={loading} disabled={uploadErrors.length} />
          </Form>
        </Container>
      </BaseModal>
      <SelectMediaModal
        modalRef={selectMediaModalRef}
        influencerId={influencerId}
        tag={tags}
        multiple
        onAdd={onSelectMedia}
        reload={reload}
        maxSelectedItems={10}
        exactMatch
      />
    </>
  );
};

export default UploadBundleModal;
